/** The itinerary record's header (plan §2 "Header", §5 "Header") — the activity record
 *  header's pattern on `PageHeader`:
 *
 *  - Title = the name the app shows (never "null": `Itinerary #<id>`).
 *  - Beside it, neutral outlined chips with an icon and a word: the status — **Live** /
 *    **Coming soon** / **Not published** / **Placeholder** (`liveVerdict`'s status, never
 *    "Draft"; `CatalogStatusChip`, the itineraries grid's too), its full `liveVerdict`
 *    sentence in the chip's popover with the operator's
 *    itineraries the grid groups it with (worded from the status) — and, when imported,
 *    the source ("↻ Intrepid", its `catalogSourceLabel` in the tooltip).
 *  - Subtitle: tenant · ID · identity (the custom ID in mono, or "ext ID ✓" when only the
 *    ext ID is set) · Ext IDs ›, each with its value actions.
 *  - Right: the Actions menu (its Danger group in Signal Red) and **Edit** (`E`) — or, in
 *    edit mode, **Close editor** (`Esc`). No manage permission → no Edit at all; editing
 *    switched off on the environment → Edit disabled, without its `E` hint, in a tab stop
 *    named "Edit — Editing is switched off" (the user record's blocked Edit), saying so in
 *    its tooltip and in the Actions menu's note. Another reason Edit cannot open yet
 *    (`editBlockedReason`) blocks it the same way, with its own words.
 *  - Below 1100 px of content (no dock column) a **Preview** button before them opens the
 *    phone preview in a drawer (`onPreview`). The right side wraps under the title at 390 px,
 *    and its buttons wrap among themselves before anything overflows the page.
 *  - A placeholder (O9): whatever groups the page passes, Delete is never offered and
 *    Publish now / Unpublish now and Regenerate deep link now stay in the menu, disabled, saying
 *    why (`placeholderActionGroups`, keyed by `ITINERARY_ACTION`). P0's guard and the actions'
 *    are the authority.
 *
 *  Pure over the detail payload; the page (after the record-core lift) owns the actions'
 *  handlers. */
import CheckIcon from '@mui/icons-material/Check';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, Chip, Tooltip } from '@mui/material';

import type { ItineraryDetail } from '@/api/itineraries';
import { visuallyHidden } from '@/components/BoolText';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import {
  itineraryCustomIdActions,
  itineraryExtIdsActions,
  itineraryStatusActions,
  type CatalogCtx,
} from '@/features/catalog/catalogLinks';
import { CatalogStatusChip } from '@/features/catalog/CatalogStatusChip';
import { catalogEditGate } from '@/features/catalog/editGate';
import { catalogSourceLabel, liveVerdict, PLACEHOLDER_LOCKED } from '@/features/catalog/words';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { itinerariesHref } from '@/utils/gridLinks';

/** The Actions menu items the header treats specially; the page keys its items with these. */
export const ITINERARY_ACTION = {
  publishNow: 'publish_now',
  unpublishNow: 'unpublish_now',
  regenerateDeepLink: 'regenerate_deep_link',
  delete: 'delete',
} as const;

export { PLACEHOLDER_LOCKED };

/** What the server refuses on a placeholder: publishing it, and a new deep link. */
const LOCKED_KEYS: ReadonlySet<string> = new Set([
  ITINERARY_ACTION.publishNow,
  ITINERARY_ACTION.unpublishNow,
  ITINERARY_ACTION.regenerateDeepLink,
]);

/** A placeholder's Actions: Delete is dropped (never offered — P0 refuses it anyway), and
 *  Publish now / Unpublish now and Regenerate deep link now stay, disabled, with the reason on
 *  their second line (still reachable by keyboard, so the reason is read out). A group left
 *  empty is not drawn. */
export function placeholderActionGroups(groups: RecordActionGroup[]): RecordActionGroup[] {
  return groups.map((g) => ({
    ...g,
    items: g.items
      .filter((item) => item.key !== ITINERARY_ACTION.delete)
      .map((item) =>
        LOCKED_KEYS.has(item.key)
          ? { ...item, disabled: true, description: PLACEHOLDER_LOCKED }
          : item,
      ),
  }));
}

/** The name the app shows (never empty: `Itinerary #<id>`). */
export function itineraryTitle(it: Pick<ItineraryDetail, 'id' | 'name'>): string {
  return it.name?.trim() || `Itinerary #${it.id}`;
}

const INLINE_ICON_SX = { fontSize: 14, verticalAlign: '-2px' } as const;

export interface ItineraryRecordHeaderProps {
  detail: ItineraryDetail;
  /** The active page tab's label (document title). */
  section: string;
  /** `console_itineraries_manage`: without it there is no Edit button at all. */
  canEdit: boolean;
  /** Editing is switched off on this environment (`CONSOLE_CATALOG_EDIT_ENABLED`). */
  editingDisabled?: boolean;
  /** Another reason Edit cannot open yet (the EDIT half not landed, no owning operator):
   *  Edit shown disabled with these words. */
  editBlockedReason?: string | null;
  editing?: boolean;
  onEdit?: () => void;
  onClose?: () => void;
  /** The page's groups, keyed by `ITINERARY_ACTION` where it matters; a placeholder's are
   *  passed through `placeholderActionGroups` here. */
  actionGroups?: RecordActionGroup[];
  /** Pins the source chip's relative time. */
  now?: Date;
  /** Below 1100 px of content: the Preview button opens the phone in a drawer. */
  onPreview?: () => void;
  previewOpen?: boolean;
}

export function ItineraryRecordHeader({
  detail: it,
  section,
  canEdit,
  editingDisabled = false,
  editBlockedReason = null,
  editing = false,
  onEdit,
  onClose,
  actionGroups = [],
  now,
  onPreview,
  previewOpen = false,
}: ItineraryRecordHeaderProps) {
  const ctx: CatalogCtx = { tenantId: it.tenant?.id ?? null };
  const title = itineraryTitle(it);
  const verdict = liveVerdict({
    published: it.published,
    placeholder: it.placeholder,
    has_default: it.default_itinerary.id !== null,
    show_in_catalog: it.show_in_catalog,
    show_in_region_search: it.show_in_region_search,
    show_in_trip_type_search: it.show_in_trip_type_search,
    show_in_text_search: it.show_in_text_search,
  });
  const customId = it.custom_id?.trim() || null;
  const extId = it.ext_id?.trim() || null;
  const groups = it.placeholder ? placeholderActionGroups(actionGroups) : actionGroups;

  const chips = (
    <>
      <ValueActions chip title={verdict.text} actions={itineraryStatusActions(verdict.status, ctx)}>
        <CatalogStatusChip status={verdict.status} data-itinerary-status={verdict.status} />
      </ValueActions>
      {it.source && (
        <Tooltip title={catalogSourceLabel(it.source, now)} describeChild>
          <Chip
            size="small"
            variant="outlined"
            icon={<SyncIcon />}
            label={it.source.ai_import ? 'AI import' : it.source.name}
            data-itinerary-source
          />
        </Tooltip>
      )}
    </>
  );

  const subtitle = (
    <DetailSubtitle
      nowrap
      parts={[
        it.tenant ? (
          <ValueActions
            actions={operatorActions(it.tenant, {
              href: itinerariesHref({ tenant: it.tenant.id }),
              label: 'Itineraries of this operator',
            })}
          >
            {it.tenant.name || it.tenant.code}
          </ValueActions>
        ) : (
          'Unassigned'
        ),
        <ValueActions key="id" actions={[act.copy(String(it.id), 'Copy id')]}>
          ID {it.id}
        </ValueActions>,
        customId ? (
          <ValueActions actions={itineraryCustomIdActions(customId, ctx)}>
            Custom ID{' '}
            <Box component="span" sx={{ ...MONO_SX, color: 'text.primary' }}>
              {customId}
            </Box>
          </ValueActions>
        ) : (
          extId && (
            <ValueActions actions={[act.copy(extId, 'Copy ext ID')]}>
              <span data-itinerary-ext-id>
                ext ID <CheckIcon aria-hidden sx={INLINE_ICON_SX} />
                <Box component="span" sx={visuallyHidden}>
                  set
                </Box>
              </span>
            </ValueActions>
          )
        ),
        <ValueActions key="ext-ids" actions={itineraryExtIdsActions(it.id, ctx)}>
          Ext IDs ›
        </ValueActions>,
      ]}
    />
  );

  const gate = catalogEditGate(canEdit, editingDisabled, editBlockedReason);
  const actions = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 1,
        maxWidth: '100%',
        '& .MuiButton-root': { whiteSpace: 'nowrap' },
      }}
    >
      {onPreview && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PhoneAndroidOutlinedIcon />}
          onClick={onPreview}
          aria-haspopup="dialog"
          aria-expanded={previewOpen}
          data-preview-open
        >
          Preview
        </Button>
      )}
      <RecordHeaderActions groups={groups} editing={editing} onEdit={onEdit} onClose={onClose} {...gate} />
    </Box>
  );

  return (
    <PageHeader
      helpStory="itineraries"
      section={section}
      title={title}
      titleText={title}
      titleAdornment={chips}
      subtitle={subtitle}
      actions={actions}
    />
  );
}
